import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Profile, AddressEntry } from '../types/profile';
import { ProfileFieldGroup } from '../components/features/ProfileFieldGroup';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSubscription } from '../contexts/SubscriptionContext';
import { ProfileSelector } from '../components/ProfileSelector';
import { PasswordModal } from '../components/common/PasswordModal'; // Import the PasswordModal component
import { Dialog } from '../components/common/Dialog';

// Add export to make FieldType available to other components
export type FieldType = keyof Profile | `address.${keyof AddressEntry}` | `urls.${string}`;

// Field groupings by subscription tier
const FREE_FIELDS = [
  { key: 'name', label: 'Full Name', type: 'text' },
  { key: 'friendlyName', label: 'Profile Name', type: 'text' },
  { key: 'email', label: 'Email', type: 'email' },
  { key: 'phone', label: 'Phone', type: 'tel' }
];

const PRO_FIELDS = [
  // Personal Information
  { key: 'nickname', label: 'Nickname', type: 'text' },
  { key: 'birthday', label: 'Birthday', type: 'date' },
  // Location
  { key: 'address.street', label: 'Street Address', type: 'text' },
  { key: 'address.city', label: 'City', type: 'text' },
  { key: 'address.state', label: 'State', type: 'text' },
  { key: 'address.postalCode', label: 'Postal Code', type: 'text' },
  { key: 'address.country', label: 'Country', type: 'text' },
  // Notes
  { key: 'notes', label: 'Notes', type: 'textarea' },
  // Social Media
  { key: 'facebook', label: 'Facebook', type: 'url', placeholder: 'https://facebook.com/username' },
  { key: 'instagram', label: 'Instagram', type: 'url', placeholder: 'https://instagram.com/username' },
  { key: 'youtube', label: 'YouTube', type: 'url', placeholder: 'https://youtube.com/@username' },
  // Online
  { key: 'website', label: 'Website', type: 'url', placeholder: 'https://' }
];

const BUSINESS_PRO_FIELDS = [
  // Work Information
  { key: 'jobTitle', label: 'Job Title', type: 'text' },
  { key: 'organization', label: 'Organization', type: 'text' },
  { key: 'department', label: 'Department', type: 'text' },
  { key: 'role', label: 'Role', type: 'text' },
  // Professional Networks
  { key: 'linkedin', label: 'LinkedIn', type: 'url', placeholder: 'https://linkedin.com/in/username' },
  // Social & Messaging
  { key: 'twitter', label: 'X (Twitter)', type: 'url', placeholder: 'https://x.com/username' },
  { key: 'tiktok', label: 'TikTok', type: 'url', placeholder: 'https://tiktok.com/@username' },
  { key: 'github', label: 'GitHub', type: 'url', placeholder: 'https://github.com/username' },
  { key: 'skype', label: 'Skype', type: 'text' },
  { key: 'telegram', label: 'Telegram', type: 'text' },
  { key: 'whatsapp', label: 'WhatsApp', type: 'tel' },
  // Professional Info
  { key: 'interests', label: 'Interests', type: 'text' },
  { key: 'expertise', label: 'Expertise', type: 'text' }
];

// File Sharing and Image Gallery Fields
const FILE_SHARING_FIELDS = [
  { 
    key: 'flickrGalleryUrl', 
    label: 'Flickr Image Gallery', 
    type: 'url', 
    placeholder: 'Your Flickr gallery or album URL',
    defaultLink: 'https://www.flickr.com/signup',
    viewText: 'Join/Login',
    helpText: 'Share your Flickr photos & short videos. To get your share link: Open your Flickr album → Click Share icon → Copy'
  },
  { 
    key: 'dropboxShareUrl', 
    label: 'Dropbox Sharing', 
    type: 'url', 
    placeholder: 'Your Dropbox shared folder URL',
    defaultLink: 'https://www.dropbox.com/login',
    viewText: 'Join/Login',
    helpText: 'Share files via Dropbox. To get your share link: Open Dropbox folder → Click Share → Create link → Copy'
  },
  { 
    key: 'googleDriveUrl', 
    label: 'Google Drive Files', 
    type: 'url', 
    placeholder: 'Your Google Drive shared folder URL',
    defaultLink: 'https://accounts.google.com/signup/v2/webcreateaccount',
    viewText: 'Join/Login',
    helpText: 'Share files via Google Drive. To get your share link: Open Drive folder → Click Share → Get link → Copy'
  }
];

export const Settings = (): JSX.Element => {
  const { currentProfile, profiles, updateProfile, createProfile, setCurrentProfile, user, deleteProfile } = useAuth();
  const { checkSubscriptionStatus, tier, status } = useSubscription();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [profileData, setProfileData] = useState<Profile | null>(null);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const isNewProfile = searchParams.get('action') === 'new-profile';

  useEffect(() => {
    // Reset editing state when switching profiles or modes
    setEditingField(null);
    setEditValue('');

    if (isNewProfile) {
      setProfileData({
        name: '',
        friendlyName: '',
        email: '',
        phone: '',
        isPrimary: false,
        isPublic: false
      } as Profile);
    } else if (currentProfile) {
      setProfileData(currentProfile);
    }
  }, [currentProfile, isNewProfile, searchParams]);

  // Handle subscription success
  useEffect(() => {
    const success = searchParams.get('success');
    const sessionId = searchParams.get('session_id');
    
    if (success === 'true' && sessionId) {
      checkSubscriptionStatus().then(() => {
        setShowSuccess(true);
        setTimeout(() => {
          navigate('/settings', { replace: true });
          setShowSuccess(false);
        }, 5000);
      });
    }
  }, [searchParams, navigate, checkSubscriptionStatus]);

  const handleFieldEdit = (field: FieldType) => {
    let currentValue = '';
    
    if (field.startsWith('address.')) {
      const addressField = field.split('.')[1] as keyof AddressEntry;
      // More robust handling of address field values
      currentValue = profileData?.address?.[0] 
        ? String(profileData.address[0][addressField] ?? '')
        : '';
    } else {
      currentValue = String(profileData?.[field as keyof Profile] || '');
    }

    setEditingField(field);
    setEditValue(currentValue);
  };

  const handleSave = async () => {
    if (!editingField || !profileData) return;
    
    setIsSaving(true);
    try {
      let isNewProfile = false;
      let currentProfileData = profileData;
      
      // If this is a new profile action, create it first
      if (searchParams.get('action') === 'new-profile') {
        try {
          const newProfile = await createProfile({
            ...currentProfileData,
            [editingField]: editValue
          });
          setCurrentProfile(newProfile);
          isNewProfile = true;
          currentProfileData = newProfile;
          // Remove the new-profile action from URL
          navigate('/settings', { replace: true });
        } catch (createError) {
          console.error('Profile creation error:', createError);
          throw createError; // Re-throw to be caught by outer catch
        }
      }

      // Create a clean copy of the profile data without old structures
      const cleanProfileData = { ...currentProfileData };
      // Clean up legacy structures
      if ('gdriveUrl' in cleanProfileData) {
        delete cleanProfileData.gdriveUrl;
      }
      if ('dropboxUrl' in cleanProfileData) {
        delete cleanProfileData.dropboxUrl;
      }
      if ('flickrUrl' in cleanProfileData) {
        delete cleanProfileData.flickrUrl;
      }

      const updatedData = { ...cleanProfileData };

      if (editingField.startsWith('address.')) {
        const addressField = editingField.split('.')[1] as keyof AddressEntry;
        updatedData.address = updatedData.address || [{}];
        updatedData.address[0] = {
          ...updatedData.address[0],
          [addressField]: editValue
        };
      } else {
        (updatedData as any)[editingField] = editValue;
      }

      // If we just created the profile, it's already updated with the initial field
      if (!isNewProfile || profileData._id) {
        await updateProfile(updatedData);
        setProfileData(updatedData);
      }

      setEditingField(null);
      setEditValue('');
    } catch (err) {
      console.error('Profile save error:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteProfile = async () => {
    if (!currentProfile?._id || profiles.length <= 1) return;
    
    try {
      await deleteProfile(currentProfile._id);
      // Navigate to the first available profile
      const remainingProfile = profiles.find(p => p._id !== currentProfile._id);
      if (remainingProfile) {
        navigate('/settings');
      }
    } catch (error) {
      console.error('Error deleting profile:', error);
    }
  };

  const isBusinessPro = tier === 'BUSINESS_PRO';

  return (
    <div className="container mx-auto px-4 py-8">
      {showSuccess && (
        <div className="mb-8 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">
                Subscription Activated Successfully!
              </h3>
              <div className="mt-2 text-sm text-green-700">
                <p>Your {tier} subscription is now {status}.</p>
                <p>You now have access to all {tier} features.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-3xl mx-auto space-y-8">
        <div className="border-b pb-4">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Settings</h2>
            <button
              onClick={() => setIsPasswordModalOpen(true)}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              {user?.googleId ? 'Set Password' : 'Change Password'}
            </button>
          </div>
          <ProfileSelector 
            profiles={profiles} 
            currentProfile={currentProfile} 
            setCurrentProfile={setCurrentProfile}
          />
          {profiles.length > 1 && currentProfile && !isNewProfile && (
            <div className="mt-4">
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="text-red-600 hover:text-red-800 text-sm font-medium"
              >
                Delete Profile
              </button>
            </div>
          )}
        </div>

        <div className="space-y-8">
          <ProfileFieldGroup
            title="Basic Information"
            tier="FREE"
            fields={FREE_FIELDS}
            profileData={profileData}
            onEdit={handleFieldEdit}
          />

          <ProfileFieldGroup
            title="Pro Features"
            tier="PRO"
            fields={PRO_FIELDS}
            profileData={profileData}
            onEdit={handleFieldEdit}
          />

          {isBusinessPro && (
            <>
              <ProfileFieldGroup
                title="Business Pro Features"
                tier="BUSINESS_PRO"
                fields={BUSINESS_PRO_FIELDS}
                profileData={profileData}
                onEdit={handleFieldEdit}
              />
              <div className="mt-8">
                <h3 className="text-lg font-medium text-gray-700 mb-4">File Sharing & Image Gallery</h3>
                <p className="text-sm text-gray-500 italic mb-4">Included in Business Pro</p>
                <ProfileFieldGroup
                  title=""
                  tier="BUSINESS_PRO"
                  fields={FILE_SHARING_FIELDS}
                  profileData={profileData}
                  onEdit={handleFieldEdit}
                />
              </div>
            </>
          )}
        </div>

        {editingField && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-medium mb-4">
                Edit {editingField.includes('.') ? editingField.split('.')[1] : editingField}
              </h3>
              
              <input
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                className="w-full p-2 border rounded mb-4"
                autoFocus
              />

              <div className="flex space-x-2">
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className={`flex-1 p-2 text-white rounded ${
                    isSaving ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => {
                    setEditingField(null);
                    setEditValue('');
                  }}
                  className="flex-1 p-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
                  disabled={isSaving}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <PasswordModal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
      />
      <Dialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Delete Profile"
      >
        <div className="p-6">
          <p className="text-gray-600 mb-4">
            Are you sure you want to delete this profile? This action cannot be undone.
          </p>
          <div className="flex justify-end gap-4">
            <button
              onClick={() => setShowDeleteConfirm(false)}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                handleDeleteProfile();
                setShowDeleteConfirm(false);
              }}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Delete
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
